import { useFormik } from "formik";
import { closeOutline } from "ionicons/icons";
import { ReactElement, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  ModalOverlay,
  ModalCard as KitModalCard,
  ModalTitle,
  ModalActions,
  ModalPrimaryButton,
  ModalFields,
} from "../../atoms/ModalKit";
import Icon from "../../atoms/Icon";
import { TextField } from "../../atoms/LoginTextField";
import SelectField from "../../atoms/SelectField";
import * as Yup from "yup";
import {
  InvitationChannelEnum,
  sendInvitationFormType,
} from "../../../types/invitation.types";
import { IonModal } from "@ionic/react";
import { getStudyLangs } from "../../../services/study.service";
import { useConfig } from "../../../contexts/ConfigContext";

interface InvitationFormModalProps {
  onSaveClick: (values: sendInvitationFormType) => void;
  channel: InvitationChannelEnum;
  isOpen: boolean;
  onDismiss: () => void;
}

const InvitationFormModal = ({
  channel,
  onSaveClick,
  isOpen,
  onDismiss,
}: InvitationFormModalProps): ReactElement => {
  const handleSubmit = async (values: sendInvitationFormType) => {
    await onSaveClick(values);
    onDismiss();
  };

  const formSchema = useMemo(
    () =>
      Yup.object().shape({
        firstname: Yup.string().required(),
        lastname: Yup.string().required(),
        email: Yup.string().when([], {
          is: () => channel === InvitationChannelEnum.EMAIL,
          then: Yup.string().email().required(),
        }),
        phone_number: Yup.string().when([], {
          is: () => channel === InvitationChannelEnum.SMS,
          then: Yup.string().required(),
        }),
        lang: Yup.string().required(),
      }),
    [channel]
  );

  const { values, touched, errors, submitForm, setFieldValue } =
    useFormik<sendInvitationFormType>({
      initialValues: {
        firstname: "",
        lastname: "",
        email: "",
        phone_number: "",
        lang: "",
      },
      onSubmit: handleSubmit,
      validationSchema: formSchema,
    });

  const { t } = useTranslation();

  const { study } = useConfig();

  const [langs, setLangs] = useState<string[]>([]);

  useEffect(function () {
    loadLangs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadLangs() {
    const { data } = await getStudyLangs(study.id);
    const list = [data.primary_lang, ...data.additional_langs];
    setLangs(list);
    // A single-language study offers no real choice — set it and hide the picker
    // (the language selector only makes sense when there is more than one).
    if (list.length === 1) {
      setFieldValue("lang", list[0]);
    }
  }

  return (
    <IonModal
      isOpen={isOpen}
      className="resonance-modal"
      backdropDismiss={true}
      onWillDismiss={onDismiss}
    >
      <ModalOverlay onClick={onDismiss}>
        <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{t(`invite_modal_${channel}`)}</ModalTitle>

        <CancelButton icon={closeOutline} onClick={onDismiss} />

        <ModalFields>
          <TextField
            type="text"
            onChange={(text) => setFieldValue("firstname", text)}
            value={values.firstname}
            error={touched.firstname && errors.firstname}
            placeholder={t("common_name")}
            label={t("common_name")}
          />
          <TextField
            type="text"
            onChange={(text) => setFieldValue("lastname", text)}
            value={values.lastname}
            error={touched.lastname && errors.lastname}
            placeholder={t("common_surname")}
            label={t("common_surname")}
          />

          {channel === InvitationChannelEnum.EMAIL && (
            <TextField
              type="text"
              onChange={(text) => setFieldValue("email", text)}
              value={values.email}
              error={touched.email && errors.email}
              placeholder={t("common_mail")}
              label={t("common_mail")}
            />
          )}

          {channel === InvitationChannelEnum.SMS && (
            <TextField
              type="text"
              onChange={(text) => setFieldValue("phone_number", text)}
              value={values.phone_number}
              error={touched.phone_number && errors.phone_number}
              placeholder={t("common_phone")}
              label={t("common_phone")}
            />
          )}

          {/* Only offer the language picker when the study actually has more than
              one language; a single-language study auto-selects it (see loadLangs).
              The placeholder is a real prompt ("Choisir") — passing the label text
              here too showed "Langue" stacked over "Langue". */}
          {langs.length > 1 && (
            <SelectField
              onChange={(text) => setFieldValue("lang", text)}
              value={values.lang}
              error={touched.lang && errors.lang}
              label={t("common_lang")}
              placeholder={t("invite_modal_lang_placeholder")}
              options={langs.map((lang) => ({
                text: t(`lang_${lang}`),
                id: lang,
              }))}
            />
          )}
        </ModalFields>
        <ModalActions>
          <ModalPrimaryButton onClick={submitForm}>
            {t("common_send")}
          </ModalPrimaryButton>
        </ModalActions>
        </ModalCard>
      </ModalOverlay>
    </IonModal>
  );
};

/* Wide form card — ModalOverlay flex-centers it (the pattern every other dialog
   uses); position:relative anchors the corner close button. */
const ModalCard = styled(KitModalCard)`
  position: relative;
  max-width: 600px;
`;

const CancelButton = styled(Icon)`
  position: absolute;
  top: 10px;
  right: 10px;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.45);
  font-size: 20px;
  &:active {
    opacity: 0.5;
  }
`;

export default InvitationFormModal;
